import { type Ref, defineComponent, unref, createApp, ref, computed } from 'vue-demi'
import { type ExperienceActivationPayload } from '../../../../../../types/models'
import { type IVueActivationDetailsRenderer } from './activation-details-renderer'
import { SdvActivationDetails } from '@source-digital/embedder-components-vue'
import { type ExtractProps } from '../../../../../../types/helper'
import { type MaybeRef, type UseWindowSizeReturn, type UseElementBoundingReturn } from '@vueuse/core'
import { type EmbedElementContext } from '../../../../../element-embedder'
import { type DirectLayoutModeConfig } from '../../../settings'
import { type IDetailsOverlayTarget } from '../targets/details-overlay-target'
import { getDetailsStyles } from './details-style'
import { type ActivationAnalyticsEvent } from '@source-digital/activation-list-vue'
import { type IAnalytics } from '../../../analytics/analytics'

export class DefaultActivationDetailsRenderer implements IVueActivationDetailsRenderer {
  currentActivation: Ref<ExperienceActivationPayload | undefined>
  currentActivationPosition: Ref<{ position: string, target: string } | undefined>
  currentActivationStartTime: number | undefined
  show: Ref<boolean>
  detailsStylesRef: Ref<ReturnType<typeof getDetailsStyles>>
  detailsTargetEl: Ref<HTMLElement | undefined>
  isOpen: Ref<boolean> = computed(() => this.show.value)
  constructor (
    public ctx: EmbedElementContext,
    public currentLayout: Ref<DirectLayoutModeConfig>,
    public windowSize: UseWindowSizeReturn,
    public videoElBounding: UseElementBoundingReturn,
    public detailsTargetMap: Map<string, IDetailsOverlayTarget | undefined>,
    public rootClass: MaybeRef<string>,
    getItemPositionAndTarget: (item: ExperienceActivationPayload) => { position: string, target: string } | undefined,
    public createElement: (...args: Parameters<typeof ctx.createElement>) => ReturnType<typeof ctx.createElement>,
    public hide: Ref<boolean>,
    public analytics: IAnalytics
  ) {
    this.currentActivation = ref<ExperienceActivationPayload>()
    this.show = ref(false)
    this.detailsStylesRef = computed(() => getDetailsStyles(this.currentLayout.value.details?.target, this))
    this.currentActivationPosition = computed(() => {
      if (this.currentActivation.value == null) return undefined
      const { activation } = this.currentActivation.value
      if (activation == null) return undefined
      return getItemPositionAndTarget(this.currentActivation.value)
    })
    this.initialize()
  }

  initialize () {
    const el = this.createElement('div', undefined, { role: 'details' })
    const app = createApp(this.#createComponent(this.show, this.currentActivation))
    document.body.appendChild(el)
    app.config.performance = true
    app.mount(el)
  }

  #createComponent (show: MaybeRef<boolean>, activationPayloadRef: Ref<ExperienceActivationPayload | undefined>) {
    const attrs = this.ctx.getComponentMarkerAttributes('details')
    const defaultBlockStyle = computed(() => this.currentLayout.value.details?.blockStyle)
    return defineComponent({
      name: 'EmbedderActivationDetails',
      provide: {
        smartblockOptions: {
          defaultBlockStyle
        }
      },
      render: h => {
        if (this.hide.value) return h()
        if (!unref(show) || activationPayloadRef.value == null) return h()
        return h('div', {
          attrs,
          style: this.detailsStylesRef.value.container
        }, [
          h(SdvActivationDetails, {
            class: this.rootClass,
            props: {
              activation: activationPayloadRef.value?.activation,
              context: activationPayloadRef.value?.context
            } satisfies ExtractProps<typeof SdvActivationDetails>,
            style: this.detailsStylesRef.value.inner,
            on: {
              analytics: (data: ActivationAnalyticsEvent) => {
                this.analytics.sendActivationAnalytics(data.event, data.activation, data.payload)
              },
              close: () => {
                this.close()
              }
            }
          })
        ])
      }
    })
  }

  open ({ activation, context }: ExperienceActivationPayload): () => void {
    this.currentActivationStartTime = Date.now()
    this.currentActivation.value = { activation, context }
    this.show.value = true
    return () => {
      this.close()
    }
  }

  close () {
    const activation = this.currentActivation.value
    const timestamp = this.currentActivationStartTime ? Math.floor((Date.now() - this.currentActivationStartTime) / 1000) : undefined
    const payload = { timestamp }
    this.ctx.eventEmitter.emit('activation', { event: 'closed', activation: activation?.activation, context: activation?.context })
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-non-null-assertion
    this.analytics.sendActivationAnalytics('hide', activation!.activation!, payload)
    this.show.value = false
    this.currentActivation.value = undefined
    this.currentActivationStartTime = undefined
  }
}
